import { useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ExternalLink, Pencil, Trash2 } from 'lucide-react';
import { deleteAgreement, setAgreementStatus } from '@mkcore/engagements/schema';
import { Button, Card, ErrorState, PageHeader, Skeleton } from '@mkcore/ui';
import { useActionMutation, useErrorMessage } from '@/lib/mutation';
import { ReminderButton } from '@/features/collab/reminders';
import { useCan } from '@/lib/session';
import { ConfirmButton } from '@/components/confirm';
import { AgreementDialog, AgreementStatusBadge, SignDialog, agreementQuery, dateOrDash } from '@/features/engagements/agreements';

export const Route = createFileRoute('/_app/agreements/$id')({ component: AgreementPage });

/** One agreement (CO-0173 .. CO-0176, CO-0491): details, key dates, status steps and the agreements under it. */
function AgreementPage() {
  const { id } = Route.useParams();
  const { t } = useTranslation();
  const message = useErrorMessage();
  const canUpdate = useCan('engagements:update');
  const canDelete = useCan('engagements:delete');
  const navigate = useNavigate();
  const q = useQuery(agreementQuery(id));
  const [dialog, setDialog] = useState<'edit' | 'sign' | null>(null);
  const status = useActionMutation(setAgreementStatus, { invalidate: [['engagement']] });
  const remove = useActionMutation(deleteAgreement, { invalidate: [['engagement']], success: t('agreements.deleted'), onSuccess: () => void navigate({ to: '/agreements' }) });
  if (q.isError) return <ErrorState title={t('errors.generic')} description={message(q.error)} />;
  if (!q.data) return <Skeleton className="h-96" />;
  const a = q.data;
  const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div className="grid grid-cols-[11rem_1fr] gap-2 text-sm">
      <dt className="text-fg-3">{label}</dt>
      <dd>{children}</dd>
    </div>
  );

  return (
    <>
      <PageHeader
        eyebrow={t('agreements.title')}
        title={a.title}
        description={`${a.organisationName} · ${t(`agreements.kind.${a.kind}`)}`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <AgreementStatusBadge status={a.status} />
            <ReminderButton
              targetType="agreement"
              targetId={a.id}
              title={a.title}
              path={`/agreements/${a.id}`}
              dates={[
                { label: t('agreements.fields.noticeOn'), date: a.noticeOn },
                { label: t('agreements.fields.endsOn'), date: a.endsOn },
              ]}
            />
            {canUpdate && a.status !== 'ended' && (
              <>
                <Button variant="secondary" onClick={() => setDialog('edit')}>
                  <Pencil /> {t('common.edit')}
                </Button>
                {a.status === 'draft' && (
                  <Button variant="secondary" loading={status.isPending} onClick={() => status.mutate({ input: { id: a.id, status: 'sent' } })}>
                    {t('agreements.markSent')}
                  </Button>
                )}
                {a.status !== 'signed' && <Button onClick={() => setDialog('sign')}>{t('agreements.sign')}</Button>}
                <ConfirmButton
                  label={t('agreements.end')}
                  variant="danger"
                  title={t('agreements.endTitle', { title: a.title })}
                  description={t('agreements.endText')}
                  confirmLabel={t('agreements.end')}
                  onConfirm={() => status.mutateAsync({ input: { id: a.id, status: 'ended' } })}
                />
              </>
            )}
            {canDelete && !a.signedOn && (
              <ConfirmButton
                icon={<Trash2 />}
                label={t('common.delete')}
                variant="danger"
                title={t('agreements.deleteTitle', { title: a.title })}
                description={t('agreements.deleteText')}
                confirmLabel={t('common.delete')}
                onConfirm={() => remove.mutateAsync({ input: { id: a.id }, ifMatch: a.version })}
              />
            )}
          </div>
        }
      />
      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="p-4">
          <h2 className="mb-3 text-lg font-bold">{t('agreements.details')}</h2>
          <dl className="grid gap-2">
            <Row label={t('agreements.fields.organisation')}>
              <Link to="/org/$id" params={{ id: a.organisationId }} className="underline">
                {a.organisationName}
              </Link>
            </Row>
            {a.engagementId && (
              <Row label={t('agreements.fields.engagement')}>
                <Link to="/engagements/$id" params={{ id: a.engagementId }} className="underline">
                  {a.engagementName}
                </Link>
              </Row>
            )}
            <Row label={t('agreements.fields.partyRole')}>{t(`agreements.party.${a.partyRole}`)}</Row>
            <Row label={t('agreements.fields.reference')}>{a.reference ?? '–'}</Row>
            <Row label={t('agreements.fields.owner')}>{a.ownerName ?? '–'}</Row>
            <Row label={t('agreements.fields.startsOn')}>{dateOrDash(a.startsOn)}</Row>
            <Row label={t('agreements.fields.endsOn')}>{dateOrDash(a.endsOn)}</Row>
            <Row label={t('agreements.fields.noticeOn')}>{dateOrDash(a.noticeOn)}</Row>
            <Row label={t('agreements.fields.renewalOn')}>{dateOrDash(a.renewalOn)}</Row>
            <Row label={t('agreements.fields.autoRenew')}>{a.autoRenew ? t('agreements.renewsBy', { months: a.renewalMonths }) : t('common.no')}</Row>
            <Row label={t('agreements.fields.signedOn')}>{a.signedOn ? `${dateOrDash(a.signedOn)} · ${t(`agreements.via.${a.signedVia ?? 'wet'}`)}` : '–'}</Row>
            {a.endedOn && <Row label={t('agreements.fields.endedOn')}>{dateOrDash(a.endedOn)}</Row>}
            <Row label={t('agreements.fields.documentUrl')}>
              {a.documentUrl ? (
                <a href={a.documentUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 underline">
                  {t('agreements.openDocument')} <ExternalLink className="size-3" />
                </a>
              ) : (
                '–'
              )}
            </Row>
          </dl>
          {a.notes && <p className="mt-3 whitespace-pre-wrap text-sm text-fg-2">{a.notes}</p>}
        </Card>
        <Card className="p-4">
          <h2 className="mb-3 text-lg font-bold">{t('agreements.related')}</h2>
          {a.parentId && (
            <p className="mb-3 text-sm">
              {t('agreements.fields.parent')}:{' '}
              <Link to="/agreements/$id" params={{ id: a.parentId }} className="underline">
                {a.parentTitle}
              </Link>
            </p>
          )}
          {a.children.length === 0 ? (
            <p className="text-sm text-fg-3">{t('agreements.noChildren')}</p>
          ) : (
            <ul className="grid gap-1 text-sm" aria-label={t('agreements.children')}>
              {a.children.map((c) => (
                <li key={c.id} className="flex items-center justify-between gap-2">
                  <Link to="/agreements/$id" params={{ id: c.id }} className="underline">
                    {c.title}
                  </Link>
                  <span className="text-fg-3">
                    {t(`agreements.kind.${c.kind}`)} · <AgreementStatusBadge status={c.status} />
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
      {dialog === 'edit' && <AgreementDialog agreement={a} onClose={() => setDialog(null)} />}
      {dialog === 'sign' && <SignDialog agreement={a} onClose={() => setDialog(null)} />}
    </>
  );
}
